import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { SeverityBadge } from "@/components/common/SeverityBadge";
import { Bug, Search, ExternalLink, Filter, ShieldCheck, RefreshCw, Download, FileCode } from "lucide-react";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";
import { downloadCsv, VULN_COLUMNS } from "@/lib/exportCsv";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const FILTERS = ["all", "critical", "high", "medium", "low"];

const remediationFor = (v) => {
  if (v.severity === "critical" || v.severity === "high")
    return `Patch prioritaire : mets à jour ${v.service || "le service"} sur ${v.host}:${v.port}. Restreins l'accès réseau tant que le correctif n'est pas appliqué.`;
  if (v.severity === "medium")
    return `Planifie la mise à jour de ${v.service || "ce service"} et vérifie sa configuration (ports exposés, versions).`;
  return `Surveille ${v.service || "ce service"} et applique les mises à jour de routine.`;
};

const cveLink = (id) => id.startsWith("CVE-") ? `https://nvd.nist.gov/vuln/detail/${id}` : `https://www.google.com/search?q=${encodeURIComponent(id)}`;

const detailLink = (v) => {
  if ((v.source || "").startsWith("searchsploit") && (v.id || "").startsWith("EDB-"))
    return `https://www.exploit-db.com/exploits/${v.id.replace("EDB-", "")}`;
  if (v.id?.startsWith("CVE-")) return `https://nvd.nist.gov/vuln/detail/${v.id}`;
  if (v.matched_at && /^https?:\/\//.test(v.matched_at)) return v.matched_at;
  return cveLink(v.id || "");
};

const prettyPath = (v) => {
  if (!v.matched_at) return null;
  if ((v.source || "").startsWith("searchsploit"))
    return v.matched_at.replace("/opt/exploitdb/", "").replace(/^exploits\//, "");
  return v.matched_at;
};

export default function Vulnerabilities() {
  const [vulns, setVulns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [sev, setSev] = useState("all");
  const { user } = useAuth();
  const canExport = user?.plan === "pro";

  const exportCsv = () => {
    if (!canExport) { toast.message("Export réservé au Pro", { description: "Passe à la formule Pro pour exporter en CSV." }); return; }
    downloadCsv(`vulnerabilites-${new Date().toISOString().slice(0,10)}.csv`, filtered, VULN_COLUMNS);
  };

  const load = () => { setLoading(true); api.getVulnerabilities().then(setVulns).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => vulns.filter(v => {
    const okSev = sev === "all" || v.severity === sev;
    const okQ = !q.trim() || (`${v.id}${v.title}${v.host}${v.service}`).toLowerCase().includes(q.toLowerCase());
    return okSev && okQ;
  }), [vulns, q, sev]);

  return (
    <div className="px-4 md:px-8 py-8 max-w-[1600px] mx-auto space-y-6 animate-fade-in" data-testid="vulnerabilities-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="font-mono text-xs text-primary tracking-widest mb-2">// MODULE 04 · VULNERABILITIES</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight flex items-center gap-3"><Bug className="h-8 w-8 text-primary"/> CVE correlation feed</h1>
          <p className="text-muted-foreground mt-1 text-sm">{filtered.length} résultat(s) · corrélés via nmap vulners / NSE.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportCsv} disabled={filtered.length === 0} className="font-mono text-xs gap-2" data-testid="export-csv-btn"><Download className="h-3.5 w-3.5"/>EXPORT CSV{!canExport && <span className="text-primary">(PRO)</span>}</Button>
          <Button variant="outline" onClick={load} className="font-mono text-xs gap-2"><RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")}/>ACTUALISER</Button>
        </div>
      </div>

      <Card className="bg-card/60 border-border p-4 flex flex-col md:flex-row md:items-center gap-3">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
          <Input data-testid="vuln-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Chercher CVE, hôte, service..." className="pl-9 bg-background border-border focus-visible:ring-primary font-mono text-sm"/>
        </div>
        <div className="flex items-center gap-1.5">
          <Filter className="h-3.5 w-3.5 text-muted-foreground mr-1"/>
          {FILTERS.map(f => (
            <button key={f} data-testid={`vuln-filter-${f}`} onClick={() => setSev(f)} className={cn(
              "px-3 py-1.5 rounded font-mono text-[10px] tracking-widest uppercase border transition-colors",
              sev === f ? "bg-primary/15 text-primary border-primary/50" : "text-muted-foreground border-border hover:border-primary/30"
            )}>{f}</button>
          ))}
        </div>
      </Card>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((v, i) => (
          <Card key={`${v.id}-${v.host}-${v.port}-${i}`} data-testid={`vuln-card-${v.id}`} className="bg-card/60 border-border p-5 flex flex-col hover:border-primary/40 transition-colors">
            <div className="flex items-start justify-between gap-2 mb-3">
              <div className="min-w-0">
                <div className="font-mono text-xs text-primary">{v.id}</div>
                <div className="font-semibold text-sm leading-snug mt-1">{v.title}</div>
              </div>
              <SeverityBadge level={v.severity}/>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs font-mono border-t border-border pt-3">
              <div><div className="text-muted-foreground text-[10px] tracking-widest">HOST</div><div className="text-foreground truncate">{v.host}:{v.port}</div></div>
              <div><div className="text-muted-foreground text-[10px] tracking-widest">SERVICE</div><div className="text-foreground uppercase">{v.service || "—"}</div></div>
              <div><div className="text-muted-foreground text-[10px] tracking-widest">CVSS</div><div className="text-warning font-bold">{Number(v.cvss || 0).toFixed(1)} / 10</div></div>
              <div><div className="text-muted-foreground text-[10px] tracking-widest">SOURCE</div><div className="text-foreground truncate">{v.source}</div></div>
            </div>
            <div className="mt-3 pt-3 border-t border-border">
              <div className="font-mono text-[10px] tracking-widest text-success mb-1 flex items-center gap-1.5"><ShieldCheck className="h-3 w-3"/>REMÉDIATION</div>
              <p className="text-xs text-muted-foreground leading-snug">{remediationFor(v)}</p>
            </div>
            {prettyPath(v) && (
              <div className="mt-3 pt-3 border-t border-border" data-testid={`vuln-path-${v.id}`}>
                <div className="font-mono text-[10px] tracking-widest text-warning mb-1 flex items-center gap-1.5">
                  <FileCode className="h-3 w-3"/>{(v.source || "").startsWith("searchsploit") ? "EXPLOIT-DB" : "URL"}
                </div>
                <code className="text-[11px] text-foreground/90 break-all font-mono block bg-background border border-border rounded px-2 py-1.5">{prettyPath(v)}</code>
              </div>
            )}
            <div className="mt-4 flex items-center justify-between text-xs font-mono text-muted-foreground">
              <span className="uppercase">{v.state}</span>
              <span>{v.scanned_at ? new Date(v.scanned_at).toLocaleDateString() : ""}</span>
            </div>
            <div className="mt-4 pt-2 mt-auto">
              <a href={detailLink(v)} target="_blank" rel="noreferrer">
                <Button size="sm" variant="outline" className="w-full font-mono text-xs gap-2 border-border"><ExternalLink className="h-3.5 w-3.5"/>{(v.source || "").startsWith("searchsploit") ? "VOIR L'EXPLOIT" : "DÉTAILS & CORRECTIF"}</Button>
              </a>
            </div>
          </Card>
        ))}
      </div>

      {!loading && filtered.length === 0 && (
        <Card className="bg-card/60 border-border p-12 text-center" data-testid="vuln-empty">
          <Bug className="h-8 w-8 text-muted-foreground mx-auto mb-3"/>
          <div className="text-muted-foreground font-mono text-sm">Aucune vulnérabilité. Lance un scan avec la corrélation CVE activée.</div>
        </Card>
      )}
    </div>
  );
}
